// Jours d'une location : bornes d'« à facturer » et aperçu du calcul pendant
// la saisie (QA Jordan 19.10, location 107). Miroir de location/jours.py
// (bornes, appliquer_jours, totaux) — le serveur reste la référence et
// recalcule tout à l'enregistrement.

const MASTER = 2
const TOLERANCE = 0.01
const rond = (v) => Math.round(v * 100) / 100

const restituee = (loc, etatRestitue) =>
	(!!loc.restitue || loc.etat >= etatRestitue) &&
	(parseInt(loc.nbJoursReels) || 0) > 0

// Jordan 26.10 : héritée d'avant GE-63 (aucun retour prévu mémorisé) —
// « à facturer » changé = adoptée par le calcul des jours. Celles de la
// tablette 3.10 restent verrouillées.
export const adoptable = (loc) => !!loc.jours_herites && !loc.dateRestiPrevue

// (bas, haut) d'« à facturer », ou null (pas restituée, réels manquants,
// héritée : pas tenue par le serveur — sauf `adoption`, saisie en cours sur
// une héritée adoptable). Jordan 30.10 : `large` (boss, ou valeur inchangée)
// → de 0 à max(prévus, réels) ; sinon de prévus à max(prévus, réels).
export const bornesAFacturer = (
	loc,
	etatRestitue,
	adoption = false,
	large = false,
) => {
	if (!restituee(loc, etatRestitue)) return null
	if (loc.jours_herites && !(adoption && adoptable(loc))) return null
	if (loc.isPret) return { bas: 0, haut: 0 }
	const prevus = parseInt(loc.nbJours) || 0
	return {
		bas: large ? 0 : prevus,
		haut: Math.max(prevus, parseInt(loc.nbJoursReels)),
	}
}

const vide = (v) => v === null || v === undefined || v === ""

// Message sous le champ si la saisie sort des bornes, sinon null. Comme le
// serveur (jours.valider_a_facturer) : bornes du rôle pour un CHANGEMENT,
// bornes larges pour le boss ou une valeur égale à celle enregistrée
// (`enBase`, posée par le boss sous les prévus).
export const erreurAFacturer = (
	loc,
	etatRestitue,
	adoption = false,
	{ boss = false, enBase } = {},
) => {
	const v = Number(loc.nbJoursAFacturer)
	const large = boss || (!vide(enBase) && v === Number(enBase))
	const b = bornesAFacturer(loc, etatRestitue, adoption, large)
	if (!b) return null
	// Revue 30.10 (J1) : un non-boss ne vide pas la valeur posée par le boss
	// sous les prévus (même règle que le serveur).
	if (vide(loc.nbJoursAFacturer)) {
		const prevus = parseInt(loc.nbJours) || 0
		return !boss && !loc.isPret && !vide(enBase) && Number(enBase) < prevus
			? `Valeur du boss (${enBase}) : ne se vide pas`
			: null
	}
	if (Number.isInteger(v) && v >= b.bas && v <= b.haut) return null
	return b.bas === b.haut
		? `${b.bas} jour${b.bas > 1 ? "s" : ""} seulement`
		: `Entre ${b.bas} et ${b.haut} jours`
}

// Ligne jours figée (location/jours.cause_figee) : encaissée, moyen posé
// (hors perte), WinBIZ, facture, quittance.
const figee = (t) =>
	!!t.date ||
	(!!t.moyenPaie && t.payeParSelect !== MASTER) ||
	!!(t.extra_data || {}).winbiz ||
	!!t.facture ||
	!!t.quittance

const nature = (t) => (t.extra_data || {}).location_jours

// Aperçu du calcul d'appliquer_jours pour la saisie en cours → {
// joursFactures, supplement, perte, totalSupplements, totalFacture,
// totalOffert, tropPercu } ou null (pas de bornes, ou saisie invalide).
// `serveur` : lignes ENREGISTRÉES (une ligne de supplément encaissée ne bouge
// jamais : l'écart en trop devient le trop-perçu). Jordan 26.10 :
// totalFacture = total de la fiche (location prévue + suppléments), quel
// que soit le payeur.
// Jordan 30.10 : bornes larges (comme appliquer_jours) ; sous les prévus, le
// supplément est NÉGATIF (part client des jours), la perte = réels − af.
export const apercuJours = (loc, serveur, etatRestitue, adoption = false) => {
	const b = bornesAFacturer(loc, etatRestitue, adoption, true)
	if (!b || erreurAFacturer(loc, etatRestitue, adoption, { boss: true }))
		return null
	const af = vide(loc.nbJoursAFacturer)
		? b.haut
		: Number(loc.nbJoursAFacturer)
	const prevus = parseInt(loc.nbJours) || 0
	const reels = parseInt(loc.nbJoursReels) || 0
	const tarif = parseFloat(loc.tarifJourna) || 0
	const supplement = rond((loc.isPret ? 0 : af - prevus) * tarif)
	const perte = rond((loc.isPret ? reels : Math.max(0, reels - af)) * tarif)
	const totalSupplements = rond(
		supplement +
			perte +
			(parseFloat(loc.supp_carburant) || 0) +
			(parseFloat(loc.supp_degats) || 0),
	)
	const encaisse = rond(
		(serveur || [])
			.filter((t) => nature(t) === "supplement" && figee(t))
			.reduce((s, t) => s + (parseFloat(t.total) || 0), 0),
	)
	// Jordan 30.10 : un supplément négatif (crédit à la main) n'est pas un
	// trop-perçu sur la ligne encaissée.
	const trop = rond(encaisse - Math.max(0, supplement))
	// Pertes hors « jours » (carburant, dégâts, gestes) : lignes Perte de la
	// saisie, inchangées ; la perte « jours » suit le calcul.
	const autresPertes = (loc.transactionsv2 || [])
		.filter((t) => t.payeParSelect === MASTER && nature(t) !== "perte")
		.reduce((s, t) => s + (parseFloat(t.total) || 0), 0)
	const totalOffert = rond(autresPertes + perte)
	return {
		joursFactures: af,
		supplement,
		perte,
		totalSupplements,
		totalOffert,
		totalFacture: rond((parseFloat(loc.total) || 0) + totalSupplements),
		tropPercu: trop > TOLERANCE ? trop : 0,
	}
}

// Jordan 27.10 : « Suppléments » éclaté en trois cases (carte Tarifs) → {
// joursEnPlus, joursOfferts, carburant, degats, total }. Jours en plus =
// (réels − prévus) × tarif = part client + part Master (« dont X offerts »,
// null si sans objet) ; total = total_supplements tel que le serveur le
// recalculera (carburant / dégâts de la saisie en cours). Ancienne fiche non
// adoptée, ou pas encore restituée : valeurs enregistrées.
export const casesSupplements = (loc, etatRestitue, adoption = false) => {
	const carburant = rond(parseFloat(loc.supp_carburant) || 0)
	const degats = rond(parseFloat(loc.supp_degats) || 0)
	const stocke = rond(parseFloat(loc.total_supplements) || 0)
	const a =
		loc.jours_herites && !adoption
			? null
			: apercuJours(loc, [], etatRestitue, adoption)
	if (!a)
		return {
			joursEnPlus: loc.jours_herites
				? rond(parseFloat(loc.supp_jours) || 0)
				: rond(Math.max(0, stocke - carburant - degats)),
			joursOfferts: null,
			carburant,
			degats,
			total: stocke,
		}
	return {
		joursEnPlus: rond(a.supplement + a.perte),
		joursOfferts: a.perte,
		carburant,
		degats,
		total: a.totalSupplements,
	}
}

// GE-78 (Jordan 27.10) : le serveur ne tient plus les lignes jours à chaque
// sauvegarde. « À facturer » changé (ou adoption) → la confirmation propose
// de créer / ajuster la ligne de supplément et la ligne offerte → {
// supplement: {montant, actuel, figee}, perte: {…}, deja, base, couvert }
// ou null (rien à confirmer). `serveur` : lignes ENREGISTRÉES. Cases
// décochées par défaut si les transactions couvrent déjà le total.
export const lignesJoursAConfirmer = (
	loc,
	serveur,
	etatRestitue,
	adoption = false,
) => {
	const a = apercuJours(loc, serveur, etatRestitue, adoption)
	if (!a) return null
	const lignes = (serveur || []).filter((t) => t.id)
	const ligne = (n, montant) => {
		const l = lignes.filter((t) => nature(t) === n)
		return {
			montant,
			actuel: rond(l.reduce((s, t) => s + (parseFloat(t.total) || 0), 0)),
			// Ligne payée (supplément) : ne bouge jamais ; la perte suit
			// (boss) — cf. location/jours.appliquer_jours.
			figee: n === "supplement" && l.some(figee),
		}
	}
	const deja = rond(
		lignes.reduce((s, t) => s + (parseFloat(t.total) || 0), 0),
	)
	return {
		supplement: ligne("supplement", a.supplement),
		perte: ligne("perte", a.perte),
		deja,
		base: a.totalFacture,
		couvert: deja >= a.totalFacture - TOLERANCE,
	}
}

// Jordan 26.10 : confirmation de l'adoption d'une héritée → lignes créées
// (jours extra répartis client / Master) et suppléments avant → après (le
// serveur les recalcule : jours extra + carburant + dégâts). Revue 26.10
// (L1) : une part saisie à la main, non ventilée dans supp_jours /
// supp_carburant / supp_degats, sera remplacée → `manuels`. Doublon possible
// si les transactions déjà présentes couvrent plus que le total actuel de la
// fiche (location prévue + suppléments actuels). Rien ne bloque : le bureau
// décide.
export const adoptionHeritee = (loc, etatRestitue) => {
	const a = apercuJours(loc, [], etatRestitue, true)
	if (!a) return null
	const prevus = parseInt(loc.nbJours) || 0
	const reels = parseInt(loc.nbJoursReels) || 0
	const lignes = (loc.transactionsv2 || []).filter((t) => t.id)
	const deja = rond(
		lignes.reduce((s, t) => s + (parseFloat(t.total) || 0), 0),
	)
	const actuels = rond(parseFloat(loc.total_supplements) || 0)
	const ventiles = rond(
		(parseFloat(loc.supp_jours) || 0) +
			(parseFloat(loc.supp_carburant) || 0) +
			(parseFloat(loc.supp_degats) || 0),
	)
	const manuels = rond(actuels - ventiles)
	const base = rond((parseFloat(loc.total) || 0) + actuels)
	return {
		joursExtra: Math.max(0, reels - prevus),
		tarif: parseFloat(loc.tarifJourna) || 0,
		montant: rond(a.supplement + a.perte),
		client: a.supplement,
		master: a.perte,
		supplementsAvant: actuels,
		supplementsApres: a.totalSupplements,
		manuels: manuels > TOLERANCE ? manuels : 0,
		dejaFactures: deja > base + TOLERANCE ? lignes : [],
		deja,
		base,
	}
}
